/* eslint-disable complexity */
import { connect } from 'react-redux';
import { createStructuredSelector, createSelector } from 'reselect';
import Empty from 'constants/empty';
import { constructorVsMLSelector } from 'viewModel/selectors/testTarget/constructorVsMLSelector';
import { bestSellersMLSelector } from 'selectors/sephoraML/bestSellersMLSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import ConstructorRecsSelector from 'selectors/constructorRecs/constructorRecsSelector';
import { isShopYourStoreNewAndBestEnabledSelector } from 'viewModel/selectors/shopYourStore/isShopYourStoreNewAndBestEnabledSelector';
import { isTestTargetReadySelector } from 'viewModel/selectors/testTarget/isTestTargetReadySelector';
import { isStoreIdAndZipCodeSettledSelector } from 'viewModel/selectors/user/isStoreIdAndZipCodeSettledSelector';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import sysUtils from 'utils/ShopYourStore';
import SephoraMLActions from 'actions/SephoraMLActions';
import catalogActions from 'actions/CatalogActions';
import ConstructorRecsActions from 'actions/ConstructorRecsActions';
import StoreAggregationActions from 'actions/StoreAggregationActions';
import userActions from 'actions/UserActions';
import { ML_VS_CONSTRUCTOR } from 'constants/TestTarget';
import { NEW_CONTENT_PAGE_ID, BESTSELLERS_PAGE_ID, RESULTS_COUNT } from 'constants/constructorConstants';

const { wrapHOC } = FrameworkUtils;
const { constructorRecsSelector } = ConstructorRecsSelector;
const { updateRequestData } = ConstructorRecsActions;
const { getTextFromResource, getLocaleResourceFile } = LanguageLocaleUtils;
const { getBestSellersRecs } = SephoraMLActions;
const getText = getLocaleResourceFile('components/Content/ContentLayout/LayoutProductGrid/locales', 'LayoutProductGrid');
const getTextNoResults = getLocaleResourceFile('components/Catalog/ProductGrid/NoResults/locales', 'NoResults');

const withLayoutProductGridProps = wrapHOC(
    connect(
        createSelector(
            coreUserDataSelector,
            constructorRecsSelector,
            createStructuredSelector({
                showMore: getTextFromResource(getText, 'showMore'),
                breadcrumb: getTextFromResource(getText, 'breadcrumb'),
                viewing: getTextFromResource(getText, 'viewing'),
                results: getTextFromResource(getText, 'results'),
                noResults1: getTextFromResource(getTextNoResults, 'noResults1'),
                noResults2: getTextFromResource(getTextNoResults, 'noResults2')
            }),
            constructorVsMLSelector,
            bestSellersMLSelector,
            (_state, ownProps) => ownProps.content,
            (_state, ownProps) => ownProps.sasData,
            isShopYourStoreNewAndBestEnabledSelector,
            isTestTargetReadySelector,
            isStoreIdAndZipCodeSettledSelector,
            (
                user,
                constructorRecs,
                localization,
                constructorMLExperience,
                bestSellersML,
                content,
                sasData,
                isShopYourStoreNewAndBestEnabled,
                isTestTargetReady,
                isStoreIdAndZipCodeSettled
            ) => {
                const podId = content?.ctorPodId || '';
                let recs = null;
                let layoutExperience = '';

                const isNewContentPage =
                    content?.sid?.includes(NEW_CONTENT_PAGE_ID) || Object.keys(constructorRecs)?.[0]?.includes(NEW_CONTENT_PAGE_ID);
                const isCollection = !!isNewContentPage;
                const {
                    isError = true,
                    isEmpty = true,
                    constructorRecommendations = null,
                    totalResults = null,
                    resultId = null
                } = constructorRecs[podId] || Empty.Object;

                if (constructorMLExperience === ML_VS_CONSTRUCTOR.ML && bestSellersML.fetchFailed === false) {
                    layoutExperience = constructorMLExperience;
                    recs = bestSellersML.items;
                } else {
                    layoutExperience = ML_VS_CONSTRUCTOR.CONSTRUCTOR;

                    if (isError || isEmpty) {
                        recs = content?.skuList || Empty.Array;
                    } else {
                        recs = constructorRecommendations;
                    }
                }

                const isLoading = recs === null || constructorRecs[podId]?.isLoading;
                const isNewConstructorPage = podId.startsWith(NEW_CONTENT_PAGE_ID);
                const isBestsellersConstructorPage = podId.startsWith(BESTSELLERS_PAGE_ID);
                const shouldFetchDataFromSAS = isShopYourStoreNewAndBestEnabled && (isNewConstructorPage || isBestsellersConstructorPage);
                let sasProps = {};

                if (shouldFetchDataFromSAS) {
                    const isSasInitialized = sasData?.isSasInitialized ?? false;
                    const items = sasData?.items || Empty.Array;

                    sasProps = {
                        layoutExperience: null, // Clear layoutExperience because data comes from SAS instead of Constructor
                        isSasInitialized,
                        shouldFetchDataFromSAS,
                        contextId: sasData?.contextId,
                        isLoading: !isSasInitialized,
                        deliveryOptions: sasData?.deliveryOptions,
                        recs: items,
                        showLoadMoreProductsBtn: sasData?.lastFetchedItemsCount === RESULTS_COUNT.CONTENT_PAGE_SIZE,
                        refinements: sasData?.refinements,
                        zeroResults: !!(isSasInitialized && !recs.length),
                        totalResults: items.length,
                        resultId: sasData?.constructor?.result_id
                    };
                }

                return {
                    user,
                    localization,
                    layoutExperience,
                    recs,
                    podId,
                    isNewContentPage,
                    isLoading,
                    bestSellersMLFailed: bestSellersML.fetchFailed === true,
                    totalResults,
                    resultId,
                    isCollection,
                    isNewConstructorPage,
                    isBestsellersConstructorPage,
                    isTestTargetReady,
                    isStoreIdAndZipCodeSettled,
                    shopYourStoreNewBestEnabled: sysUtils.shopYourStoreNewBestEnabled(),
                    ...sasProps
                };
            }
        ),
        {
            getBestSellersRecs,
            updateRequestData,
            onStoreChangedFromHeader: userActions.onStoreChangedFromHeader,
            onZipCodeChangedFromHeader: userActions.onZipCodeChangedFromHeader,
            fetchNewProducts: StoreAggregationActions.fetchNewProducts,
            fetchBestsellersProducts: StoreAggregationActions.fetchBestsellersProducts,
            applyFilters: catalogActions.applyFilters
        }
    )
);

export { withLayoutProductGridProps };
